import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  Unidad,
  UnidadResidente,
  CreateUnidadRequest,
  UpdateUnidadRequest,
  VinculoUnidad,
  CrearVinculoRequest,
  CerrarVinculoRequest,
} from '@condo/contracts';
import { ApiRoutes } from '@condo/contracts';
import { api } from '@/lib/api/client';
import { queryKeys } from '@/lib/api/query-keys';

export interface UnidadConContexto {
  unidad: Unidad;
  residentesActivos: UnidadResidente[];
  vinculosActivos: VinculoUnidad[];
}

export interface HistorialItem {
  asignacion: UnidadResidente;
  residente: {
    id: string;
    nombre: string;
    apellidos: string;
    email: string;
    rut: string;
  };
}

interface UnidadesListResponse {
  items: Unidad[];
}

interface HistorialResponse {
  items: HistorialItem[];
}

export interface UnidadesFilters {
  tipo?: Unidad['tipo'];
  piso?: number;
  activo?: boolean;
  conResidentesActivos?: boolean;
}

export function useUnidades(condoId: string, filters: UnidadesFilters = {}) {
  const qs = new URLSearchParams();
  if (filters.tipo) qs.set('tipo', filters.tipo);
  if (filters.piso !== undefined) qs.set('piso', String(filters.piso));
  if (filters.activo !== undefined) qs.set('activo', String(filters.activo));
  if (filters.conResidentesActivos) qs.set('conResidentesActivos', 'true');
  const suffix = qs.toString() ? `?${qs.toString()}` : '';

  return useQuery({
    queryKey: queryKeys.unidades(condoId, filters as Record<string, unknown>),
    queryFn: () => api.get<UnidadesListResponse>(ApiRoutes.unidades(condoId) + suffix),
    enabled: !!condoId,
  });
}

export function useUnidad(condoId: string, id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.unidad(condoId, id ?? ''),
    queryFn: () => api.get<UnidadConContexto>(ApiRoutes.unidadById(condoId, id!)),
    enabled: !!condoId && !!id,
  });
}

export function useHistorialUnidad(condoId: string, id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.unidadHistorial(condoId, id ?? ''),
    queryFn: () => api.get<HistorialResponse>(ApiRoutes.unidadHistorial(condoId, id!)),
    enabled: !!condoId && !!id,
  });
}

export function useCreateUnidad(condoId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateUnidadRequest) =>
      api.post<Unidad, CreateUnidadRequest>(ApiRoutes.unidades(condoId), input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.unidades(condoId) });
    },
  });
}

export function useUpdateUnidad(condoId: string, id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateUnidadRequest) =>
      api.patch<Unidad, UpdateUnidadRequest>(
        ApiRoutes.unidadById(condoId, id),
        input,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.unidades(condoId) });
      void qc.invalidateQueries({ queryKey: queryKeys.unidad(condoId, id) });
    },
  });
}

export function useDesactivarUnidad(condoId: string, id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api.post<Unidad, Record<string, never>>(
        ApiRoutes.unidadDesactivar(condoId, id),
        {},
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.unidades(condoId) });
      void qc.invalidateQueries({ queryKey: queryKeys.unidad(condoId, id) });
    },
  });
}

interface VinculosListResponse {
  items: VinculoUnidad[];
}

export function useVinculos(condoId: string, viviendaId: string | undefined) {
  const qs = viviendaId ? `?viviendaId=${viviendaId}` : '';
  return useQuery({
    queryKey: queryKeys.vinculos(condoId, viviendaId),
    queryFn: () => api.get<VinculosListResponse>(ApiRoutes.vinculos(condoId) + qs),
    enabled: !!condoId && !!viviendaId,
  });
}

export function useCrearVinculo(condoId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CrearVinculoRequest) =>
      api.post<VinculoUnidad, CrearVinculoRequest>(ApiRoutes.vinculos(condoId), input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['condo', 'vinculos'] });
      void qc.invalidateQueries({ queryKey: ['condo', 'unidades'] });
    },
  });
}

export function useCerrarVinculo(condoId: string, vinculoId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CerrarVinculoRequest) =>
      api.post<VinculoUnidad, CerrarVinculoRequest>(
        ApiRoutes.vinculoCerrar(condoId, vinculoId),
        input,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['condo', 'vinculos'] });
      void qc.invalidateQueries({ queryKey: ['condo', 'unidades'] });
    },
  });
}
